import { observer } from "mobx-react-lite";
import { TextSort } from "../../helpers/SorterHelper";
import { DownOutlined, UpOutlined, EyeOutlined, DeleteOutlined, EditOutlined, SaveOutlined, CloseOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { useStore } from "../../stores/root-store-context";
import { useEffect, useState } from "react";
import { getUserComponent } from "../../helpers/user";
import { useTranslation } from "react-i18next";
import { Button, Flex, Form, FormProps, Steps, Modal, Popconfirm, Tooltip, Spin, Table, TableProps, Tag, Typography, App, Tabs, DatePicker, InputNumber, Select, Avatar, Row, Col, Dropdown, Input } from "antd";
import { Test, TestAttempt, UserTestAssignment } from "../../stores/TestsStore";
import { toJS } from "mobx";
import 'dayjs/locale/uk';
import dayjs, { Dayjs } from 'dayjs';
import { GoogleGenerativeAI } from "@google/generative-ai";
import isBetween from "dayjs/plugin/isBetween";
import { SkillLevelName } from "../../helpers/skills";
import { TestGeneralAssignModal, TestGenerationRecomendedTest } from "./TestGeneralAssignModal";
import { TestAttemptModal } from "./TestAttemptModal";
import { useNavigate } from "react-router-dom";
import { TestHelper } from "../../helpers/testHelper";
import { TestSelect } from "./TestSelect";
import { Skill } from "../../models/Skill";
import { Employee } from "../../models/Employee";
import { Languages } from "../../enums/Languages";
import { AssignmentStatus } from "../../enums/AssignmentStatus";

dayjs.extend(isBetween);
const { TabPane } = Tabs;
const { Step } = Steps;

export interface TestRow {
    key: number;
    level: string;
    skill: string;
    id: number;
}

export interface TestUserPlanResponseGenerator {
    testId: number;
    startDate: string;
    endDate: string;
}

export const TestUsersInfo = observer(({ companyAssignments, setCompanyAssignments, userAssignments, setUserAssignments, allTests }: {
    companyAssignments: UserTestAssignment[],
    userAssignments: UserTestAssignment[],
    allTests: Test[],
    setCompanyAssignments: (userAssignments: UserTestAssignment[]) => void,
    setUserAssignments: (userAssignments: UserTestAssignment[]) => void
}
) => {
    const GEMINI_MODEL = `${process.env.REACT_APP_AI_GEMINI_MODEL}`;
    const API_AI_KEY = `${process.env.REACT_APP_AI_GEMINI_API_KEY}`;
    const { t } = useTranslation();
    const { testsStore } = useStore();
    const { applicationStore, employeesStore } = useStore();
    const [expandedSkills, setExpandedSkills] = useState<{ [key: string]: boolean }>({});
    const [filteredUser, setFilteredUser] = useState<Employee[]>([]);
    const [expandedRowKey, setExpandedRowKey] = useState<string[]>([]);
    const [isModalVisibleAttempt, setIsModalVisibleAttempt] = useState(false);
    const [isModalVisibleTests, setIsModalVisibleTests] = useState(false);
    const [filteredAllTests, setFilteredAllTests] = useState<Test[]>(allTests);
    const [isModalVisibleGeneralAssignTests, setIsModalVisibleGeneralAssignTests] = useState(false);
    const [generationSuggestTests, setGenerationSuggestTests] = useState<Test[]>([]);
    const [selectedTestId, setSelectedTestId] = useState<number | null>(null);
    const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);
    const [selectedTestLanguages, setSelectedTestLanguages] = useState<string[]>([]);
    const [selectedAttempts, setSelectedAttempts] = useState<TestAttempt[]>([]);
    const [selectedUser, setSelectedUser] = useState<Employee | null>(null);
    const [selectedUserAssignments, setSelectedUserAssignments] = useState<UserTestAssignment[]>([]);
    const { message } = App.useApp();
    const navigate = useNavigate();
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
    const [selectedAssignmentUserTestPlan, setSelectedAssignmentUserTestPlan] = useState<UserTestAssignment[]>([]);
    const [editingIdAssignment, setEditingIdAssignment] = useState<number | null>(null);
    const [editedAssignment, setEditedAssignment] = useState<UserTestAssignment | null>(null);
    const [currentPageAssignment, setCurrentPageAssignment] = useState(1);
    const pageSizeAssignmentTable = 5;

    useEffect(() => {
        setLoading(true);

        const fetchData = async () => {

            const allCompanyTestAssignments = await testsStore.getAllAssignmentByCompanyId(applicationStore.user.companyId.toString());
            setCompanyAssignments(allCompanyTestAssignments);

            setFilteredUser(toJS(applicationStore.users).filter((u: Employee) => u.isActive && u.isApproveInvite));

            setLoading(false);
        }

        fetchData();
    }, []);

    const isEditing = (record: UserTestAssignment) => record.id == editingIdAssignment;

    const handleChangePage = (page: number) => {
        setCurrentPageAssignment(page);
    };

    const showAttempts = (attempts: TestAttempt[]) => {
        setSelectedAttempts(attempts);
        setIsModalVisibleAttempt(true);
    };

    const handleModalCloseAttempt = () => {
        setIsModalVisibleAttempt(false);
        setSelectedAttempts([]);
    };

    const handleLanguageChange = (value: string) => {
        setSelectedLanguage(value);
    };

    const handleTestChange = (value: number) => {
        setSelectedTestId(value);

        const selectedTest = allTests.find((test) => test.id == value);

        if (selectedTest) {
            if (!applicationStore?.siteConfig?.isShowLanguage) {
                setSelectedTestLanguages([Languages.EN]);
                setSelectedLanguage(Languages.EN);
            }
            else if (selectedTest.language == "all") {
                setSelectedTestLanguages([Languages.ANY, Languages.EN, Languages.RU, Languages.UK]);
                setSelectedLanguage(Languages.ANY);
            } else {
                setSelectedTestLanguages(selectedTest.language == Languages.EN ? [Languages.EN] : selectedTest.language == Languages.UK ? [Languages.UK] : [Languages.RU]);
                setSelectedLanguage(selectedTest.language);
            }
        } else {
            setSelectedTestLanguages([]);
            setSelectedLanguage(null);
        }
    };

    const generationTestPlanForUser = async () => {
        if (selectedUser && selectedUser.userSkills.length > 0) {
            const planTestArray = await generationPlanTestForUser(selectedUser);

            const userTestPlanAssignmentsArray: UserTestAssignment[] = planTestArray.map((item, index) => {
                const existTest = allTests.find(t => t.id == item.testId);
                return {
                    id: index,
                    language: (existTest && existTest?.language == "all") ? Languages.ANY : existTest?.language ?? Languages.ANY,
                    test: existTest ?? null,
                    testId: item.testId,
                    numberOfAttempts: existTest?.numberOfAttempts ?? 2,
                    finishAssignedDate: item.endDate,
                    startAssignedDate: item.startDate,
                    assignedDate: new Date().toISOString(),
                    assignmentStatusId: 1,
                    assignedToUser: null,
                    assignmentStatus: null,
                    assignedToUserId: Number(selectedUser.id),
                    assignedByUserId: Number(applicationStore.user.id),
                    testAttempts: []
                };
            });

            setSelectedAssignmentUserTestPlan(userTestPlanAssignmentsArray);
        }
        else {
            if (selectedUser?.userSkills.length == 0) {
                message.error(t("tests.no_skills"));
            }
        }

        return [];
    };

    const handleUpdateTestPlan = (testPlanId: number, field: keyof UserTestAssignment, value: any) => {
        setSelectedAssignmentUserTestPlan(prevTestPlans => {
            const newTestPlans = prevTestPlans.map(testPlan =>
                testPlan.id == testPlanId ? { ...testPlan, [field]: value } : testPlan
            );
            return [...newTestPlans];
        });
    };


    const handleAddTestPlan = () => {
        setSelectedAssignmentUserTestPlan(prevTestPlans => {
            const newId = prevTestPlans.length > 0 ? Math.max(...prevTestPlans.map(test => test.id)) + 1 : 1;

            const newTestPlan: UserTestAssignment = {
                id: newId,
                testId: allTests.length > 0 ? allTests[0].id : 0,
                language: "all",
                startAssignedDate: dayjs().format("YYYY-MM-DD"),
                finishAssignedDate: dayjs().add(1, 'day').format("YYYY-MM-DD"),
                numberOfAttempts: allTests.find(t => t.id == allTests[0]?.id)?.numberOfAttempts ?? 2,
                assignedDate: new Date().toISOString(),
                assignmentStatusId: 1,
                test: null,
                assignedToUser: null,
                assignmentStatus: null,
                assignedToUserId: Number(selectedUser?.id),
                assignedByUserId: Number(applicationStore.user.id),
                testAttempts: []
            };

            return [...prevTestPlans, newTestPlan];
        });
    };

    const handleDeleteTestPlan = (testId: number) => {
        const updatedPlan = selectedAssignmentUserTestPlan.filter(test => test.id != testId);
        setSelectedAssignmentUserTestPlan(updatedPlan);
    };

    const skillLevelMapping = (level: number) => {
        if (level <= 2) return 'Junior';
        if (level <= 4) return 'Middle';
        return 'Senior';
    };

    const generationPlanTestForUser = async (user: Employee) => {
        setLoading(true);

        const transformedSkills = user.userSkills.map(skill => {
            const isConfirmed = skill.isConfirmed ?? false;
            const name = skill.skill.title;
            const levelName = skillLevelMapping(Number(skill.skillLevel));
            return { name, levelName, isConfirmed };
        });

        const allTestsFilterForUser = TestHelper.filterAvailableTests(allTests, [], transformedSkills);

        const transformedAllTests = allTestsFilterForUser.map(test => {
            return { id: test.id, name: test.name, levelName: test.level, skillName: applicationStore.skills.find(s => s.skillId == test.skillId?.toString())?.title };
        });

        const jsonTransformedSkills = JSON.stringify(transformedSkills, null, 2);
        const jsonTransformedAllTests = JSON.stringify(transformedAllTests, null, 2);

        const requestAI = `
            Based on the following data about the user's skills, their levels, and skill confirmation status, as well as the list of all available tests, create a test plan for the user.
            The plan should be designed for the next year starting from the first day of the next month, ensuring that the user takes a new test every 3 months (4 tests in total).

            ### Test Selection Criteria:
            - If a user's skill is confirmed (IsConfirmed = true) and there exists a test for the next level (e.g., Junior → Middle, Middle → Senior), propose only the higher-level test.
            - If a skill is rated as Middle or Senior, only suggest tests that match this level.
            - If a skill does not exist for the user, only propose tests for the Junior level.
            - Ensure that the same test is not repeated for the same skill within the plan.
            - If no relevant tests are available for the user's current skills, propose new tests for technologies not yet in the user's skill set (Junior level).

            ### Input Data:
            Current date: ${new Date().toISOString()}
            
            Example data structure:
            User (skills):
            ${jsonTransformedSkills}

            All available tests:
            ${jsonTransformedAllTests}

            Please create a test plan for the next 12 months starting from the first day of the next month with a 3-month interval (March, June, September, December), including start and end dates for each test.
            The response must be a JSON array of objects with the following structure:
            [
                {
                    "testId": Test ID,
                    "startDate": Start date,
                    "endDate": End date
                }
            ]
            No additional information.
        `;

        try {
            const genAI = new GoogleGenerativeAI(API_AI_KEY);
            const model = genAI.getGenerativeModel({ model: GEMINI_MODEL });

            const resultResponse = await model.generateContent(requestAI);

            const rawResponse = resultResponse.response.text().trim();
            const jsonStart = rawResponse.indexOf("[");
            const jsonEnd = rawResponse.lastIndexOf("]") + 1;

            if (jsonStart === -1 || jsonEnd === 0) {
                throw new Error("Response does not contain a valid JSON array.");
            }

            const cleanedResponse = rawResponse.substring(jsonStart, jsonEnd);

            const result: TestUserPlanResponseGenerator[] = JSON.parse(cleanedResponse);
            console.log("Result", result);

            return result;
        } catch (error) {
            console.error("Error fetching data from API:", error);
            return [];
        }
        finally {
            setLoading(false);
        }
    };

    const handleUpdateUserTestPlan = async (userId: number) => {
        try {

            let assignmentUserTestPlan = selectedAssignmentUserTestPlan || [];

            const userExistAssignment = companyAssignments.filter(ua => ua.assignedToUserId == Number(selectedUser?.id));

            assignmentUserTestPlan = assignmentUserTestPlan.map(testPlan => {
                const existingAssignment = userExistAssignment.find(ua => ua.id == testPlan.id);
                const existingTest = allTests.find(t => t.id == testPlan.testId);

                return {
                    ...testPlan,
                    id: existingAssignment ? testPlan.id : 0,
                    language: (existingTest && existingTest?.language == "all") ? Languages.ANY : existingTest?.language ?? Languages.ANY,
                };
            });

            const result = await testsStore.updateUserTestAssignmentsPlan(assignmentUserTestPlan, userId);

            if (selectedDate != null && selectedTestId != null && selectedLanguage != null) {
                await handleCreateAssignment();
            }

            if (result) {
                message.success(t("Success"));
            } else {
                message.error(t("Error"));
            }

        } catch (error) {
            console.log(error);
            message.error(t("Error"));
        }
    }

    const handleCreateUserTestPlan = async (userId: number) => {
        const createAssignmentsUser = selectedAssignmentUserTestPlan.map(item => {
            const existingTest = allTests.find(t => t.id == item.testId);

            return ({
                ...item,
                id: 0,
                test: null,
                language: (existingTest && existingTest?.language == "all") ? Languages.ANY : existingTest?.language ?? Languages.ANY,
            });
        });

        try {
            const result = await testsStore.addAssignmentsPlan(createAssignmentsUser, userId);

            if (result && selectedUser != null) {
                message.success(t("Success"));

                const updatedUsers = filteredUser.map(user =>
                    user.id == selectedUser?.id ? { ...user, testPlan: result.userTestPlans, isGenerationUserTestPlan: true } : user
                );

                setFilteredUser(updatedUsers);
            } else {
                message.error(t("Error"));
            }

        } catch (error) {
            console.log(error);
            message.error(t("Error"));
        }
    };

    const showModalTests = async (user: Employee) => {
        setIsModalVisibleTests(true);
        setSelectedUser(user);
        setSelectedUserAssignments(companyAssignments.filter(ua => ua.assignedToUserId == Number(user.id)));

        if (user.isGenerationUserTestPlan != null || user.isGenerationUserTestPlan == true) {
            setSelectedAssignmentUserTestPlan(companyAssignments.filter(ua => ua.assignedToUserId == Number(user.id)));
        }

        const testsForUser = await generationNeedTests(user);
        const validTestsForUser = testsForUser.filter((test): test is Test => test !== undefined);

        const filteredAllTests = allTests.filter(test =>
            !validTestsForUser.some((suggestedTest: Test) => suggestedTest.id === test.id)
        );
        setGenerationSuggestTests(validTestsForUser);
        setFilteredAllTests(filteredAllTests);
    };

    const validateAssignmentsDatesAndTest = (): boolean => {
        const testIds = new Set<number>();

        for (const assignment of selectedAssignmentUserTestPlan) {
            const startDate = new Date(assignment.startAssignedDate);
            const finishDate = new Date(assignment.finishAssignedDate);

            if (finishDate < startDate) {
                message.error(t(`tests.errorValidationDatePlan`));
                return false;
            }

            if (assignment.testId) {
                if (testIds.has(assignment.testId)) {
                    message.error(t(`tests.errorDuplicateTestId`));
                    return false;
                }
                testIds.add(assignment.testId);
            }
        }

        return true;
    };

    const handleOkTests = async () => {

        const existUser = filteredUser.find(u => u.id == selectedUser?.id);

        if (existUser && selectedAssignmentUserTestPlan != null && existUser.isGenerationUserTestPlan == true) {
            var resultValidation = validateAssignmentsDatesAndTest();

            if (!resultValidation) {
                return;
            }

            setLoading(true);
            await handleUpdateUserTestPlan(Number(existUser?.id));
        }
        else if (existUser && selectedAssignmentUserTestPlan != null && selectedAssignmentUserTestPlan.length > 0 && (existUser.isGenerationUserTestPlan == false || existUser?.isGenerationUserTestPlan == null)) {
            var resultValidation = validateAssignmentsDatesAndTest();

            if (!resultValidation) {
                return;
            }

            setLoading(true);
            await handleCreateUserTestPlan(Number(existUser?.id));
        }
        else {
            if (selectedDate != null && selectedTestId != null && selectedLanguage != null) {
                setLoading(true);
                await handleCreateAssignment();
            }
            else {
                message.error(t("tests.warningChooseAssignment"));
                return;
            }
        }

        const companyAssignments = await testsStore.getAllAssignmentByCompanyId(applicationStore.company.Id ?? "");
        setCompanyAssignments(companyAssignments);
        setUserAssignments(companyAssignments.filter(ua => ua.assignedToUserId == Number(applicationStore.user?.id)));

        setLoading(false);

        handleCancelTests();
    };

    const handleCreateAssignment = async () => {
        let finishDateTime = new Date().toISOString();

        if (selectedDate != null) {
            const combinedDateTime = selectedDate
                .hour(0)
                .minute(0)
                .second(0)
                .millisecond(0);

            finishDateTime = combinedDateTime.toISOString();
        }
        else {
            let createdDateTime = new Date();
            createdDateTime.setDate(createdDateTime.getDate() + 2);
            finishDateTime = createdDateTime.toISOString()
        }

        const existTest = companyAssignments
            .filter(a => a.assignedToUserId === Number(selectedUser?.id))
            .find(a => a.testId === selectedTestId);

        if (existTest != null) {
            message.error(t("tests.errorDuplicateTestId"));
            return;
        }

        const addAssignment: UserTestAssignment = {
            id: 0,
            test: null,
            language: selectedLanguage ?? Languages.ANY,
            testId: selectedTestId ?? 0,
            numberOfAttempts: allTests.find(t => t.id == selectedTestId)?.numberOfAttempts ?? 2,
            finishAssignedDate: finishDateTime,
            startAssignedDate: new Date().toISOString(),
            assignedDate: new Date().toISOString(),
            assignmentStatusId: 1,
            assignedToUser: null,
            assignmentStatus: null,
            assignedToUserId: Number(selectedUser?.id),
            assignedByUserId: Number(applicationStore.user.id),
            testAttempts: []
        };

        try {
            const result = await testsStore.addAssignment(addAssignment);

            if (result) {
                message.success(t("Success"));
            }
            else {
                message.error(t("Error"));
            }

        } catch (error) {
            console.log(error);
            message.error(t("Error"));
        }
    }

    const handleCancelTests = () => {
        setIsModalVisibleTests(false);
        setSelectedUser(null);
        setSelectedTestLanguages([]);
        setSelectedLanguage(null);
        setSelectedTestId(null);
        setSelectedAssignmentUserTestPlan([]);
        setSelectedUserAssignments([]);
        setFilteredAllTests(allTests);
    };

    const onExpandHandle = (expanded: boolean, record: Employee) => {
        if (expanded) {
            setExpandedRowKey([record.id]);
        } else {
            setExpandedRowKey([]);
        }
    };

    const generationNeedTests = async (user: Employee) => {
        const transformedSkills = user.userSkills.map(skill => {
            const isConfirmed = skill.isConfirmed ?? false;
            const name = skill.skill.title;
            const levelName = skillLevelMapping(Number(skill.skillLevel));
            return { name, levelName, isConfirmed };
        });

        const existUserAssignments = companyAssignments.filter(a => a.assignedToUserId == Number(user.id)).map((assignment) => {
            return { testId: assignment.testId };
        });

        const allTestsFilterForUser = TestHelper.filterAvailableTests(allTests, existUserAssignments, transformedSkills);

        const transformedAllTests = allTestsFilterForUser.map(test => {
            return { id: test.id, name: test.name, levelName: test.level, skillName: applicationStore.skills.find(s => s.skillId == test.skillId?.toString())?.title };
        });

        const jsonTransformedSkills = JSON.stringify(transformedSkills, null, 2);
        const jsonTransformedAllTests = JSON.stringify(transformedAllTests, null, 2);
        const jsonTransformedAllUserAssignments = JSON.stringify(existUserAssignments, null, 2);

        const requestAI = `
            Based on the user's skills, their levels, confirmation status, all available tests, and already assigned tests, generate a list of recommended tests for the user.

            ### Test Selection Rules:
            1. If a user's skill is **confirmed** (IsConfirmed = true) and a **higher-level test** exists (e.g., Junior → Middle, Middle → Senior), propose only the higher-level test.
            2. If a skill is **Middle or Senior**, only suggest tests that match this level.
            3. If a user **does not have a skill**, only propose a **Junior-level test**.
            4. Do not include tests that are already assigned to the user.
            5. **Prefer related (adjacent) skills** if no exact match is found.
            6. Return the result as a **JSON array** containing only test IDs.
            7. Max recommended tests it is five.

            ### Input Data:
            Current date: ${new Date().toISOString()}

            **User Skills:**
            ${jsonTransformedSkills}

            **All Available Tests:**
            ${jsonTransformedAllTests}

            **Already Assigned Tests:**
            ${jsonTransformedAllUserAssignments}

            ### Output Format:
            The response must be a **JSON array** with only the test IDs:
            [
                testId1,
                testId2,
                testId3,
                testId4,
                testId5
            ]
            No additional information.
        `;

        try {
            setLoading(true);
            const genAI = new GoogleGenerativeAI(API_AI_KEY);
            const model = genAI.getGenerativeModel({ model: GEMINI_MODEL });

            const resultResponse = await model.generateContent(requestAI);

            const rawResponse = resultResponse.response.text().trim();
            const jsonStart = rawResponse.indexOf("[");
            const jsonEnd = rawResponse.lastIndexOf("]") + 1;

            if (jsonStart === -1 || jsonEnd === 0) {
                throw new Error("Response does not contain a valid JSON array.");
            }

            const cleanedResponse = rawResponse.substring(jsonStart, jsonEnd);

            const result: number[] = JSON.parse(cleanedResponse);

            const testMap = new Map<number, Test>(allTests.map(test => [test.id, test]));
            const testsRecomended = result
                .map(recommendedTestId => testMap.get(recommendedTestId))
                .filter((test): test is Test => test !== undefined);

            setLoading(false);

            return testsRecomended;
        } catch (error) {
            console.error("Error fetching data from API:", error);
            setLoading(false);
            return [];
        }
    };

    const handleDeleteAssignment = async (assignment: UserTestAssignment) => {
        if (assignment.assignmentStatusId == AssignmentStatus.Started) {
            message.error(t("tests.You can not delete assignment"));
            return;
        }

        const result = await testsStore.deleteAssignmentById(assignment.id.toString());

        if (result) {
            message.success(t("Success"));

            const newAssignments = companyAssignments.filter(ua => ua.id !== assignment.id);
            setCompanyAssignments(newAssignments);

            const isUserAssignmentExists = userAssignments.some(ua => ua.id === assignment.id);
            if (isUserAssignmentExists) {
                const updatedUserAssignments = userAssignments.filter(ua => ua.id !== assignment.id);
                setUserAssignments(updatedUserAssignments);
            }

            if (isModalVisibleTests) {
                const newSelectedAssignments = selectedUserAssignments.filter(ua => ua.id !== assignment.id);
                setSelectedUserAssignments(newSelectedAssignments);
            }
        } else {
            message.error(t("add_test.DeleteFailed"));
        }
    };

    const toggleEditing = (id: number) => {
        if (editingIdAssignment == id) {
            setEditingIdAssignment(null);
            setEditedAssignment(null);
        } else {
            setEditingIdAssignment(id);
            setEditedAssignment(companyAssignments.find(assignment => assignment.id == id) || null);
        }
    };

    const handleEditChange = (id: number, field: string, value: any) => {
        if (editedAssignment && editedAssignment.id == id) {
            setEditedAssignment(prev => (prev ? { ...prev, [field]: value } : prev));
        }
    };

    const saveChanges = async () => {
        if (editedAssignment) {
            try {
                setLoading(true);
                await testsStore.editUserTestAssignment(editedAssignment.id, editedAssignment);

                const newCompanyAssignemnts = companyAssignments.map(assignment => assignment.id == editedAssignment.id ? editedAssignment : assignment);
                const newSelectedAssignment = selectedUserAssignments.map(assignment => assignment.id == editedAssignment.id ? editedAssignment : assignment);

                setSelectedUserAssignments(newSelectedAssignment);
                setCompanyAssignments(newCompanyAssignemnts);

                if (editedAssignment.assignedToUserId == Number(applicationStore.user.id)) {
                    const newAssignments = userAssignments.map(assignment => assignment.id == editedAssignment.id ? editedAssignment : assignment);
                    setUserAssignments(newAssignments);
                }

                setEditingIdAssignment(null);
                setEditedAssignment(null);

                message.success(t("Success"));
            } catch (error) {
                console.error("Ошибка при сохранении", error);
            } finally {
                setLoading(false);
            }
        }
    };

    const columnsAssignments = [
        {
            title: t("add_test.Name"),
            key: "titleTest",
            render: (record: UserTestAssignment) => {
                var existTest = testsStore.testsPublic.find(t => t.id == record.testId);

                if (!existTest) {
                    existTest = testsStore.testsCompany.find(t => t.id == record.testId);
                }

                return existTest ? `${applicationStore.skills.find(s => s.skillId == existTest?.skillId?.toString())?.title} - ${existTest.level}` : t("tests.no_test_name_find");
            }
        },
        {
            title: t("Description"),
            dataIndex: ['test', 'description'],
            key: 'description',
            render: (text: string) => text || t("(no description)")
        },
        {
            title: t("tests.languagePass"),
            key: 'language',
            render: (record: UserTestAssignment) => {
                const existTest = testsStore.testsPublic.find(t => t.id == record.testId) || testsStore.testsCompany.find(t => t.id == record.testId);

                if (isEditing(record) && editedAssignment != null && existTest?.language === "all") {
                    return (
                        <Select
                            value={editedAssignment.language}
                            onChange={(value) => handleEditChange(record.id, "language", value)}
                            style={{ width: "100%" }}
                        >
                            <Select.Option value="any">{t("languages.any")}</Select.Option>
                            <Select.Option value="en">{t("languages.en")}</Select.Option>
                            <Select.Option value="ru">{t("languages.ru")}</Select.Option>
                            <Select.Option value="ua">{t("languages.ua")}</Select.Option>
                        </Select>
                    );
                }

                return t(`languages.${record.language}`);
            }
        },
        {
            title: t("tests.grade"),
            key: 'grade',
            render: (record: UserTestAssignment) =>
                record.testAttempts.length > 0
                    ? `${Math.max(...record.testAttempts.map((t) => t.percentage))}/100%`
                    : "–"
        },
        {
            title: t("tests.Result"),
            key: 'result',
            render: (record: UserTestAssignment) =>
                Math.max(...record.testAttempts.map((t) => t.percentage)) > 80
                    ? t("passage_test.test_passed")
                    : t("passage_test.test_failed")
        },
        {
            title: t("add_test.Level"),
            dataIndex: ['test', 'level'],
            key: 'level'
        },
        {
            title: t("add_test.NumberAttempts"),
            dataIndex: "numberOfAttempts",
            key: "numberOfAttempts",
            render: (text: number, record: UserTestAssignment) =>
                (isEditing(record) && editedAssignment != null && editedAssignment.assignmentStatusId != AssignmentStatus.Completed) ? (
                    <InputNumber
                        min={editedAssignment.testAttempts.length + 1}
                        max={6}
                        value={editedAssignment?.numberOfAttempts}
                        onChange={(value) => handleEditChange(record.id, "numberOfAttempts", value)}
                    />
                ) : (
                    text
                ),
        },
        {
            title: t("add_test.NumberQuestions"),
            dataIndex: ['test', 'questionsToProvideCount'],
            key: 'questionsToProvideCount'
        },
        {
            title: `${t("add_test.MaxTime")} (${t("passage_test.minutes")})`,
            dataIndex: ['test', 'maxTimeMinutes'],
            key: 'maxTimeMinutes'
        },
        {
            title: t("tests.AssignedDate"),
            key: 'assignedDate',
            render: (record: UserTestAssignment) => record.assignedDate
                ? new Date(record.assignedDate).toLocaleDateString('uk-UA', {
                    day: 'numeric',
                    month: 'numeric',
                    year: 'numeric'
                }).replace(/\//g, '.')
                : t("No date")
        },
        {
            title: t("tests.selectDeadlineDate"),
            dataIndex: "finishAssignedDate",
            key: "finishAssignedDate",
            render: (text: string, record: UserTestAssignment) =>
                (isEditing(record) && editedAssignment != null && editedAssignment.assignmentStatusId != AssignmentStatus.Completed) ? (
                    <DatePicker
                        value={editedAssignment?.finishAssignedDate ? dayjs(editedAssignment.finishAssignedDate) : null}
                        format="DD.MM.YYYY"
                        onChange={(date) => handleEditChange(record.id, "finishAssignedDate", date ? date.format("YYYY-MM-DD") : null)}
                    />
                ) : (
                    text ? dayjs(text).format("DD.MM.YYYY") : t("No date")
                ),
        },
        {
            title: t("Status"),
            dataIndex: ['assignmentStatus', 'name'],
            key: 'status',
            render: (text: string) => t(text || "")
        },
        {
            title: t("Actions"),
            key: 'actions',
            render: (record: UserTestAssignment) => (
                <>
                    <Button
                        type="text"
                        shape="circle"
                        icon={<EyeOutlined />}
                        onClick={() => showAttempts(record.testAttempts)}
                        disabled={record.testAttempts.length === 0}
                        title={t("View Attempts")}
                    />
                    {isEditing(record) ? (
                        <>
                            <Button type="text" shape="circle" icon={<SaveOutlined />} onClick={saveChanges} title={t("Save")} />
                            <Button type="text" shape="circle" icon={<CloseOutlined />} onClick={() => toggleEditing(record.id)} title={t("Cancel")} />
                        </>
                    ) : (
                        record.assignmentStatusId != AssignmentStatus.Completed
                            ?
                            <Button type="text" shape="circle" icon={<EditOutlined />} onClick={() => toggleEditing(record.id)} title={t("Edit")} />
                            : null
                    )}
                    <Popconfirm
                        title={t("tests.Delete the assignment?")}
                        okText={t("Yes")}
                        cancelText={t("No")}
                        onConfirm={async () => await handleDeleteAssignment(record)}
                    >
                        <Button
                            danger
                            type="text"
                            shape="circle"
                            icon={<DeleteOutlined />}
                        />
                    </Popconfirm>
                </>
            )
        }
    ];

    const columns: TableProps<Employee>['columns'] = [
        {
            title: `${t("user.Name")}`,
            dataIndex: 'photoId',
            key: 'photo',
            render: (data, record) => <div>{getUserComponent(applicationStore.siteConfig.baseCloudinary, record, applicationStore.company?.Tenant,)}</div>,
            sorter: (a: Employee, b: Employee) => TextSort(a.firstName, b.firstName)

        },
        Table.EXPAND_COLUMN,
        {
            title: `${t("user.JobTitle")}`,
            dataIndex: 'jobTitleId',
            key: 'jobTitle',
            render: (jobTitleId) => {
                const jobTitle = employeesStore.jobTitles.find(el => el.id === jobTitleId)?.title;
                return <div>{jobTitle || t(" ")}</div>;
            },
            sorter: (a: Employee, b: Employee) => TextSort(a.jobTitleId || "", b.jobTitleId || "")
        },
        {
            title: `${t("Skills")}`,
            dataIndex: 'userSkills',
            key: 'userSkills',
            render: (skills, project) => <Flex wrap style={{ maxWidth: 200 }} gap={10}>
                {(expandedSkills[project.id] ? skills : skills.slice(0, 5)).map((el: any) => (
                    <Tooltip key={el.skill.title} title={`${t("tests.skill_level")}: ${t(SkillLevelName[el.skillLevel])}`}>
                        <Tag color={el.isConfirmed ? "green" : "blue"}>{el.skill.title}</Tag>
                    </Tooltip>
                ))}
                {skills?.length > 5 && (
                    expandedSkills[project.id] ? (
                        <Tag onClick={() => setExpandedSkills(prevState => ({
                            ...prevState,
                            [project.id]: false
                        }))} style={{ cursor: 'pointer' }} color="blue">{t("ShowLess")}</Tag>
                    ) : (
                        <Tag onClick={() => setExpandedSkills(prevState => ({
                            ...prevState,
                            [project.id]: true
                        }))} style={{ cursor: 'pointer' }} color="blue">+{skills.length - 5} more</Tag>
                    )
                )}
            </Flex>,
            sorter: (a: Employee, b: Employee) => TextSort(a.firstName, b.firstName),
            filters: applicationStore.skills?.map(skill => { return { text: skill.title, value: skill.skillId } }),
            onFilter: (value, record) => record.userSkills.some(el => el.skill.skillId === value),
        },
        {
            title: `${t("tests.assignedTests")}`,
            render: (record, project) => {
                const assignedTests = companyAssignments.filter(
                    (ua) => ua.assignedToUserId === Number(record.id)
                );
                const relevantSkills = assignedTests
                    .map((assignment) => {
                        const skillId = assignment.test?.skillId?.toString() ?? "";
                        let color = "gray";
                        let matchedSkill: Skill | null = null;
                        const existSkill = applicationStore.skills.find(s => s.skillId == skillId) ?? null;

                        if (existSkill != null) {
                            if (assignment.assignmentStatusId == AssignmentStatus.Started) {
                                color = "orange";
                            } else if (assignment.assignmentStatusId == AssignmentStatus.Completed) {
                                // const rating = matchedSkill.confirmedLevel || 0;
                                // color = rating > 4 ? "green" : "red";
                                color = "green";
                            }
                            else if (assignment.assignmentStatusId == AssignmentStatus.Expired) {
                                color = "red";
                            }
                        }

                        if (matchedSkill == null && skillId != null) {
                            const findOtherSkill = applicationStore.skills.find(s => s.skillId == skillId.toString()) ?? null;
                            matchedSkill = toJS(findOtherSkill);
                        }

                        if (!matchedSkill) {
                            return null;
                        }

                        return { ...matchedSkill, color, assignment };
                    })
                    .filter(Boolean);

                return (
                    <Flex wrap style={{ maxWidth: 200 }} gap={10}>
                        {(expandedSkills[project.id] ? relevantSkills : relevantSkills.slice(0, 5)).map((skill: any, index) => (
                            <Tooltip
                                key={`${skill.skillId}-${index}`}
                                title={
                                    <div>
                                        <div><strong>{skill.assignment.test?.name || skill.title}</strong></div>
                                        <div>{t("Level")}: {skill.assignment.test?.level || t("Unknown")}</div>
                                        <div>{t("tests.Result")}: {Math.max(...skill.assignment.testAttempts.map((t: TestAttempt) => t.percentage)) > 80
                                            ? t("passage_test.test_passed")
                                            : t("passage_test.test_failed")}
                                        </div>
                                        <div>
                                            {t("tests.selectDeadlineDate")}: {dayjs(skill.assignment.finishAssignedDate)
                                                .locale('uk')
                                                .format('DD.MM.YYYY HH:mm') || t("N/A")}
                                        </div>
                                        <div>{t("Status")}: {t(skill.assignment.assignmentStatus?.name ?? "")}</div>
                                    </div>
                                }
                            >
                                <Tag color={skill.color}>{skill.title}</Tag>
                            </Tooltip>
                        ))}
                        {relevantSkills?.length > 5 && (
                            expandedSkills[project.id] ? (
                                <Tag
                                    onClick={() => setExpandedSkills(prevState => ({
                                        ...prevState,
                                        [project.id]: false
                                    }))}
                                    style={{ cursor: 'pointer' }}
                                    color="blue"
                                >
                                    {t("ShowLess")}
                                </Tag>
                            ) : (
                                <Tag
                                    onClick={() => setExpandedSkills(prevState => ({
                                        ...prevState,
                                        [project.id]: true
                                    }))}
                                    style={{ cursor: 'pointer' }}
                                    color="blue"
                                >
                                    +{relevantSkills.length - 5} {t("more")}
                                </Tag>
                            )
                        )}
                        {relevantSkills?.length === 0 && (
                            <div>{t("tests.no_tests_assigment")}</div>
                        )}
                    </Flex>
                );
            },
            sorter: (a: Employee, b: Employee) => TextSort(a.firstName, b.firstName),
            filters: applicationStore.skills?.map(skill => { return { text: skill.title, value: skill.skillId } }),
            onFilter: (value, record) => record.userSkills.some(el => el.skill.skillId === value),
        },
    ];

    return (
        <Spin spinning={loading}>
            {applicationStore.isFreezeModalVisible ? (
                <p style={{ padding: '20px', textAlign: 'center' }}>Access to users is limited until the subscription is renewed.</p>
            ) : (
                <div>
                    <Button
                        type="primary"
                        style={{ float: "left", marginBottom: "20px" }}
                        onClick={() => setIsModalVisibleGeneralAssignTests(true)}
                    >
                        {t('tests.create_everyone')}
                    </Button>
                    <Table
                        rowKey="id"
                        columns={columns}
                        dataSource={filteredUser.filter(user => user.isActive !== false)}
                        expandable={{
                            expandedRowKeys: expandedRowKey,
                            onExpand: onExpandHandle,
                            expandIcon: ({ expanded, onExpand, record }: any) => (
                                <Button
                                    shape="circle"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onExpand(record, e);
                                    }}
                                >
                                    {expanded ? <UpOutlined /> : <DownOutlined />}
                                </Button>
                            ),
                            expandRowByClick: true,
                            expandedRowRender: (record) => {
                                return (
                                    <Tabs defaultActiveKey="1" style={{ width: "100%" }}>
                                        <TabPane tab={t(`tests.assignments`)} key={1}>
                                            <div>
                                                <Button
                                                    type="primary"
                                                    style={{ position: "absolute", top: -60, right: 0 }}
                                                    onClick={() => showModalTests(record)}
                                                >
                                                    {t('tests.assign')}
                                                </Button>

                                                <div>
                                                    {companyAssignments.filter((ua) => ua.assignedToUserId === Number(record.id)).length > 0 ? (
                                                        <Table
                                                            dataSource={companyAssignments.filter((ua) => ua.assignedToUserId === Number(record.id))}
                                                            columns={columnsAssignments}
                                                            rowKey="id"
                                                            pagination={{
                                                                current: currentPageAssignment,
                                                                pageSize: pageSizeAssignmentTable,
                                                                total: companyAssignments.filter((ua) => ua.assignedToUserId === Number(record.id)).length,
                                                                onChange: handleChangePage,
                                                            }}
                                                        />
                                                    ) : (
                                                        <p>{t("tests.no_tests_assigment")}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </TabPane>
                                    </Tabs>
                                );
                            }
                        }}
                    />
                </div>
            )}

            <TestGeneralAssignModal setAllCompanyUser={setFilteredUser} allCompanyUsers={filteredUser} allTests={testsStore.testsPublic} companyAssignments={companyAssignments} setCompanyAssignments={setCompanyAssignments} onClose={() => setIsModalVisibleGeneralAssignTests(false)} visible={isModalVisibleGeneralAssignTests} />

            <TestAttemptModal attempts={selectedAttempts} visible={isModalVisibleAttempt} onClose={handleModalCloseAttempt} />

            <Modal
                open={isModalVisibleTests}
                onOk={handleOkTests}
                onCancel={handleCancelTests}
                footer={loading ? null : undefined}
                centered
            >
                {loading ? (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '100px',
                    }}>
                        <Spin size="default" />
                    </div>
                ) : (
                    <div>
                        <div>
                            <h3 style={{ margin: 0 }}>
                                {selectedUser?.firstName} {selectedUser?.lastName}
                            </h3>
                            <p style={{ margin: "5px 0", color: "#555" }}>{selectedUser?.email}</p>
                        </div>

                        {employeesStore.jobTitles.find(el => el.id === selectedUser?.jobTitleId)?.title && (
                            <div style={{ marginBottom: "20px" }}>
                                <p style={{ margin: 0, color: "#777" }}>
                                    {employeesStore.jobTitles.find(el => el.id === selectedUser?.jobTitleId)?.title || t("tests.NotSpecified")}
                                </p>
                            </div>
                        )}

                        {selectedUser?.userSkills && (
                            <div style={{ marginBottom: "20px" }}>
                                <Flex wrap style={{ gap: "10px", maxWidth: "100%" }}>
                                    {(expandedSkills[selectedUser.id ?? '']
                                        ? selectedUser.userSkills
                                        : selectedUser.userSkills.slice(0, 5)
                                    ).map(skill => (
                                        <Tag color="blue" key={skill.skill.skillId}>
                                            {skill.skill.title}
                                        </Tag>
                                    ))}
                                    {selectedUser.userSkills.length > 5 && (
                                        expandedSkills[selectedUser.id ?? ''] ? (
                                            <Tag
                                                onClick={() =>
                                                    setExpandedSkills(prevState => ({
                                                        ...prevState,
                                                        [selectedUser.id ?? '']: false,
                                                    }))
                                                }
                                                style={{ cursor: 'pointer' }}
                                                color="blue"
                                            >
                                                Скрыть
                                            </Tag>
                                        ) : (
                                            <Tag
                                                onClick={() =>
                                                    setExpandedSkills(prevState => ({
                                                        ...prevState,
                                                        [selectedUser.id ?? '']: true,
                                                    }))
                                                }
                                                style={{ cursor: 'pointer' }}
                                                color="blue"
                                            >
                                                +{selectedUser.userSkills.length - 5} ещё
                                            </Tag>
                                        )
                                    )}
                                </Flex>
                            </div>
                        )}

                        <div>
                            {(selectedUser && selectedAssignmentUserTestPlan && selectedAssignmentUserTestPlan.length > 0) ? (
                                <div>
                                    {loading ? (
                                        <div className="text-center p-5">
                                            <Spin size="large" />
                                            <p>{t(`tests.generation`)}</p>
                                        </div>
                                    ) : (
                                        <div>
                                            {(() => {
                                                const sortedTestPlan = [...selectedAssignmentUserTestPlan].sort((a, b) =>
                                                    dayjs(a.startAssignedDate).diff(dayjs(b.startAssignedDate))
                                                );

                                                const currentIndex = sortedTestPlan.findIndex((test) => {
                                                    const start = dayjs(test.startAssignedDate);
                                                    const end = dayjs(test.finishAssignedDate);
                                                    const now = dayjs();
                                                    return now.isBetween(start, end, "day", "[]");
                                                });

                                                return (
                                                    <Steps
                                                        direction="vertical"
                                                        current={currentIndex !== -1 ? currentIndex : -1}
                                                    >
                                                        {sortedTestPlan.map((test) => (
                                                            <Step
                                                                key={test.id}
                                                                title={
                                                                    <div style={{ marginBottom: "16px", display: "flex", alignItems: "center", gap: "16px" }}>
                                                                        <Form layout="vertical" style={{ flex: 1 }}>
                                                                            <Form.Item style={{ marginBottom: "12px" }}>
                                                                                <TestSelect
                                                                                    userTestAssignment={test}
                                                                                    allTests={allTests}
                                                                                    handleUpdateTestPlan={handleUpdateTestPlan}
                                                                                />
                                                                            </Form.Item>
                                                                            <Form.Item>
                                                                                <div style={{ display: "flex", gap: "16px" }}>
                                                                                    <Form.Item style={{ flex: 1, marginBottom: 0 }}>
                                                                                        <DatePicker
                                                                                            value={dayjs(test.startAssignedDate)}
                                                                                            onChange={(date) =>
                                                                                                handleUpdateTestPlan(test.id, "startAssignedDate", date?.format("YYYY-MM-DD"))
                                                                                            }
                                                                                            style={{ width: "100%" }}
                                                                                            disabledDate={(current) => current && current.isBefore(dayjs().startOf("day"), "day")}
                                                                                        />
                                                                                    </Form.Item>
                                                                                    <span>-</span>
                                                                                    <Form.Item style={{ flex: 1, marginBottom: 0 }}>
                                                                                        <DatePicker
                                                                                            value={dayjs(test.finishAssignedDate)}
                                                                                            onChange={(date) =>
                                                                                                handleUpdateTestPlan(test.id, "finishAssignedDate", date?.format("YYYY-MM-DD"))
                                                                                            }
                                                                                            style={{ width: "100%" }}
                                                                                            disabledDate={(current) =>
                                                                                                current && (current.isBefore(dayjs(test.startAssignedDate), "day") || current.isBefore(dayjs().startOf("day"), "day"))
                                                                                            }
                                                                                        />
                                                                                    </Form.Item>
                                                                                </div>
                                                                            </Form.Item>
                                                                        </Form>
                                                                        <Button
                                                                            type="text"
                                                                            danger
                                                                            icon={<DeleteOutlined />}
                                                                            onClick={() => handleDeleteTestPlan(test.id)}
                                                                        />
                                                                    </div>
                                                                }
                                                            />
                                                        ))}
                                                    </Steps>
                                                );
                                            })()}
                                            <Button type="dashed" style={{ marginTop: "16px" }} onClick={handleAddTestPlan}>
                                                {t("add_test.addTest")}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <Button type="primary" onClick={generationTestPlanForUser}>
                                    {t("tests.create_plan")}
                                </Button>
                            )}
                        </div>

                        <div style={{ display: "flex", gap: "20px", marginBottom: "20px", marginTop: "30px" }}>
                            <div style={{ flex: 1 }}>
                                <p style={{ fontWeight: "bold" }}>{t("tests.SelectTest")}:</p>
                                <Select
                                    placeholder={t("tests.SelectTest")}
                                    style={{ width: "100%" }}
                                    value={selectedTestId}
                                    onChange={handleTestChange}
                                    showSearch
                                    filterOption={(input, option) => {
                                        const test = allTests.find((t) => t.id == option?.value);
                                        if (!test) return false;

                                        const skillTitle = applicationStore.skills.find((s) => s.skillId == test.skillId?.toString())?.title || "";
                                        return skillTitle.toLowerCase().includes(input.toLowerCase());
                                    }}
                                    optionLabelProp="label"
                                >
                                    <Select.OptGroup label={t("tests.SuggestedTests")}>
                                        {generationSuggestTests.map((test) => (
                                            <Select.Option key={test.id} value={test.id} label={`${applicationStore.skills.find(s => s.skillId == test.skillId?.toString())?.title || "unknown_skill"} - ${test.level}`}>
                                                <Row align="middle">
                                                    <Col>
                                                        <Avatar
                                                            style={{ backgroundColor: "#87d068" }}
                                                            src={TestHelper.getImageSkill(applicationStore.skills.find(s => s.skillId == test.skillId?.toString())?.title ?? "unknown_skill")}
                                                            alt="test-image"
                                                            size="large"
                                                        />
                                                    </Col>
                                                    <Col style={{ marginLeft: "10px" }}>
                                                        <strong>{applicationStore.skills.find(s => s.skillId == test.skillId?.toString())?.title || "unknown_skill"}</strong>
                                                        <div style={{ color: "#777" }}>{test.level}</div>
                                                    </Col>
                                                </Row>
                                            </Select.Option>
                                        ))}
                                    </Select.OptGroup>

                                    <Select.OptGroup label={t("tests.AllTests")}>
                                        {filteredAllTests.map((test) => (
                                            <Select.Option key={test.id} value={test.id} label={`${applicationStore.skills.find(s => s.skillId == test.skillId?.toString())?.title || "unknown_skill"} - ${test.level}`}>
                                                <Row align="middle">
                                                    <Col>
                                                        <Avatar
                                                            src={TestHelper.getImageSkill(applicationStore.skills.find(s => s.skillId == test.skillId?.toString())?.title ?? "unknown_skill")}
                                                            alt="test-image"
                                                            size="large"
                                                        />
                                                    </Col>
                                                    <Col style={{ marginLeft: "10px" }}>
                                                        <strong>{applicationStore.skills.find(s => s.skillId == test.skillId?.toString())?.title || "unknown_skill"}</strong>
                                                        <div style={{ color: "#777" }}>{test.level}</div>
                                                    </Col>
                                                </Row>
                                            </Select.Option>
                                        ))}
                                    </Select.OptGroup>
                                </Select>
                            </div>
                            <div style={{ flex: 1 }}>
                                <p style={{ fontWeight: "bold" }}>{t("tests.selectDeadlineDate")}</p>
                                <DatePicker
                                    style={{ width: "100%" }}
                                    required
                                    onChange={(value) => setSelectedDate(value)}
                                    disabledDate={(current) => current && current.isBefore(dayjs(), "day")}
                                />
                            </div>
                        </div>

                        <div>
                            {selectedTestLanguages.length > 0 && applicationStore?.siteConfig?.isShowLanguage && (
                                <div style={{ flex: 1 }}>
                                    <p style={{ fontWeight: "bold" }}>{t("tests.selectLanguage")}:</p>
                                    <Select
                                        placeholder={t("tests.selectLanguage")}
                                        style={{ width: "100%" }}
                                        value={selectedLanguage}
                                        onChange={handleLanguageChange}
                                        disabled={selectedTestLanguages.length == 1}
                                    >
                                        {selectedTestLanguages.map((lang) => (
                                            <Select.Option key={lang} value={lang}>
                                                {t(`languages.${lang}`)}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </Modal>
        </Spin>
    )
});